
// =========================
// Shopify Collection Infinite Scroll – FIXED
// =========================

// --- Move any .collection-filter to AFTER #CollectionAjaxResult ---
const filterObserver = new MutationObserver((mutations) => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (!(node instanceof HTMLElement)) return;
      if (node.classList && node.classList.contains('collection-filter')) {
        const ajaxResult = document.querySelector('#CollectionAjaxResult');
        if (ajaxResult) {
          ajaxResult.insertAdjacentElement('afterend', node);
        }
      }
    });
  });
});
filterObserver.observe(document.body, { childList: true, subtree: true });

// --- Infinite Scroll Class ---
class InfiniteScroll {
  constructor() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.ajaxResult = document.querySelector('#CollectionAjaxResult');
    this.ajaxContent = document.querySelector('#CollectionAjaxContent');
    this.productGrid = document.querySelector('#CollectionAjaxContent .grid--uniform'); // fixed
    this.pagination = document.querySelector('.pagination-wrapper, .pagination');
    this.loadingSpinner = null;
    this.totalPages = 1;

    // Number of products to load per scroll
    this.productsPerLoad = 4;
    this.remainingProducts = [];

    if (!this.ajaxResult || !this.ajaxContent || !this.productGrid) {
      console.warn('Collection elements not found. Make sure you are on a collection page.');
      return;
    }

    this.init();
  }

  init() {
    this.getTotalPages();
    this.createLoadingSpinner();
    this.setupIntersectionObserver();
    this.hidePagination();
  }

  getTotalPages() {
    if (this.pagination) {
      const pageLinks = this.pagination.querySelectorAll('a[href*="page="]');
      let maxPage = 1;

      pageLinks.forEach(link => {
        const href = link.getAttribute('href');
        const pageMatch = href.match(/page=(\d+)/);
        if (pageMatch) {
          maxPage = Math.max(maxPage, parseInt(pageMatch[1]));
        }
      });

      const nextLink = this.pagination.querySelector('a[rel="next"], .pagination__item--next a');
      if (nextLink) {
        const nextHref = nextLink.getAttribute('href');
        const nextPageMatch = nextHref.match(/page=(\d+)/);
        if (nextPageMatch) {
          maxPage = Math.max(maxPage, parseInt(nextPageMatch[1]));
        }
      }
      this.totalPages = maxPage;
    }
  }

  hidePagination() {
    if (this.pagination) {
      this.pagination.style.display = 'none';
    }
  }

  createLoadingSpinner() {
    this.loadingSpinner = document.createElement('div');
    this.loadingSpinner.className = 'infinite-scroll-loading';
    this.loadingSpinner.innerHTML = `
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading more products...</p>
      </div>`;
    this.loadingSpinner.style.display = 'none';
    this.ajaxResult.appendChild(this.loadingSpinner);
  }

  setupIntersectionObserver() {
    // Sentinel at the bottom of the product grid AFTER spinner
    const sentinel = document.createElement('div');
    sentinel.className = 'infinite-scroll-sentinel';
    sentinel.style.height = '1px';
    sentinel.style.visibility = 'hidden';
    this.loadingSpinner.insertAdjacentElement('afterend', sentinel);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadMoreProducts();
        }
      });
    }, { rootMargin: '200px' });

    observer.observe(sentinel);
  }

  async loadMoreProducts() {
    if (this.isLoading || !this.hasMorePages) return;

    this.isLoading = true;
    this.showLoading();

    try {
      if (this.remainingProducts.length > 0) {
        this.showBatch();
      } else {
        this.currentPage++;
        const response = await this.fetchProducts();
        const newProducts = this.parseResponse(response);

        if (newProducts && newProducts.length > 0) {
          this.remainingProducts = newProducts;
          this.showBatch();
        } else {
          this.hasMorePages = false;
          this.showNoMoreProducts();
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.currentPage--;
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  showBatch() {
    const batch = this.remainingProducts.splice(0, this.productsPerLoad);
    this.appendProducts(batch);

    if (this.remainingProducts.length === 0 && this.currentPage >= this.totalPages) {
      this.hasMorePages = false;
      this.showNoMoreProducts();
    }
  }

  async fetchProducts() {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('page', this.currentPage);

    const response = await fetch(currentUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.text();
  }

  parseResponse(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newAjaxContent = doc.querySelector('#CollectionAjaxContent');
    if (!newAjaxContent) return null;

    const newGrid = newAjaxContent.querySelector('.grid--uniform');
    if (!newGrid) return null;

    const products = newGrid.querySelectorAll('.grid__item.grid-product');
    return products.length > 0 ? Array.from(products) : null;
  }

  appendProducts(products) {
  const fragment = document.createDocumentFragment();

  products.forEach(product => {
    const productClone = product.cloneNode(true);

    // Initial state for the entire product card
    productClone.style.opacity = '0';
    productClone.style.transform = 'translateY(30px)';
    productClone.style.transition = 'opacity 0.5s ease, transform 0.5s ease';

    // Initial state for all images inside the product card
    const imgs = productClone.querySelectorAll('img');
    imgs.forEach(img => {
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.8s ease';
    });

    fragment.appendChild(productClone);
  });

  // Append the fragment to the product grid
  this.productGrid.appendChild(fragment);

  // Animate product cards + images
  setTimeout(() => {
    const newProducts = this.productGrid.querySelectorAll('[style*="opacity: 0"]');
    newProducts.forEach((product, index) => {
      setTimeout(() => {
        // Fade in the whole product card
        product.style.opacity = '1';
        product.style.transform = 'translateY(0)';

        // Fade in its images
        const imgs = product.querySelectorAll('img');
        imgs.forEach(img => {
          img.style.opacity = '1';
        });
      }, index * 150); // stagger effect
    });
  }, 50);

  // Reinitialize Shopify/theme scripts
  this.triggerThemeScripts();
}


  triggerThemeScripts() {
    if (window.theme && window.theme.init) window.theme.init();
    if (window.theme && window.theme.QuickShop && window.theme.QuickShop.init) window.theme.QuickShop.init();
    if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();

    if (window.theme && window.theme.Images && window.theme.Images.preload) {
      const newImages = this.productGrid.querySelectorAll('img[data-src]');
      newImages.forEach(img => window.theme.Images.preload(img));
    }

    document.dispatchEvent(new CustomEvent('infiniteScrollLoaded', {
      detail: { newProducts: this.productGrid.querySelectorAll('.grid-product') }
    }));
  }

  showLoading() {
    if (this.loadingSpinner) this.loadingSpinner.style.display = 'block';
  }

  hideLoading() {
    if (this.loadingSpinner) this.loadingSpinner.style.display = 'none';
  }

  showNoMoreProducts() {
    const noMoreMessage = document.createElement('div');
    noMoreMessage.className = 'no-more-products';
    noMoreMessage.innerHTML = `
      <div class="text-center">
        <p>You've seen all products in this collection</p>
        <p class="text-subdued">Found ${this.productGrid.querySelectorAll('.grid-product').length} products</p>
      </div>`;
    if (this.loadingSpinner && this.loadingSpinner.parentNode) {
      this.loadingSpinner.parentNode.replaceChild(noMoreMessage, this.loadingSpinner);
    } else {
      this.ajaxResult.appendChild(noMoreMessage);
    }
  }

  showError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'infinite-scroll-error';
    errorMessage.innerHTML = `
      <div class="text-center">
        <p>Oops! Something went wrong loading more products.</p>
        <button class="btn" onclick="location.reload()">Refresh page</button>
      </div>`;
    this.ajaxResult.appendChild(errorMessage);
    setTimeout(() => errorMessage.remove(), 5000);
  }
}

// CSS Styles
const infiniteScrollStyles = `
  .infinite-scroll-loading {
    text-align: center;
    padding: 60px 20px;
    margin-top: 40px;
  }
  .loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  .spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid var(--color-accent, #333);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .loading-spinner p {
    margin: 0;
    font-size: 16px;
    color: var(--color-body-text, #333);
  }
  .no-more-products {
    text-align: center;
    padding: 60px 20px;
    margin-top: 40px;
    border-top: 1px solid var(--color-border, #e8e8e8);
  }
  .no-more-products p { margin: 5px 0; }
  .text-subdued { color: var(--color-body-text, #666) !important; opacity: 0.7; font-size: 14px; }
  .infinite-scroll-error {
    background: #ff6b6b; color: white; border-radius: 4px;
    margin: 20px 0; padding: 20px; text-align: center;
  }
  .infinite-scroll-error .btn {
    background: white; color: #ff6b6b; border: none;
    padding: 10px 20px; border-radius: 4px; cursor: pointer;
    margin-top: 15px; font-weight: 600;
  }
  .infinite-scroll-error .btn:hover { background: #f8f8f8; }
  .infinite-scroll-sentinel { height: 1px !important; visibility: hidden; pointer-events: none; }
  .grid-product[style*="opacity: 0"] { pointer-events: none; }
  .grid-product[style*="opacity: 1"] { pointer-events: auto; }
`;

// Init
function initInfiniteScroll() {
  if (!document.querySelector('#CollectionSection')) return;
  const styleSheet = document.createElement('style');
  styleSheet.textContent = infiniteScrollStyles;
  document.head.appendChild(styleSheet);
  new InfiniteScroll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  initInfiniteScroll();
}

document.addEventListener('shopify:section:load', function(event) {
  if (event.target.querySelector('#CollectionSection')) {
    setTimeout(initInfiniteScroll, 100);
  }
});

